import { Button } from '@repo/ui/components/ui/button'
import { Callout } from '@repo/ui/components/ui/callout'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@repo/ui/components/ui/card'
import { Skeleton } from '@repo/ui/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/ui/table'
import { createFileRoute } from '@tanstack/react-router'
import { AlertTriangle, BarChart3 } from 'lucide-react'
import { useMemo } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ReportRouteShell } from '@/components/app/route-shells'
import type { RouteStaticData } from '@/lib/router/breadcrumbs'
import { trpc } from '@/lib/trpc'

type NotificationAnalytics = {
  total: number
  read: number
  unread: number
  clicked: number
  withAction: number
  openRate: number
  clickRate: number
  byType: Record<string, { total: number; read: number; clicked: number }>
}

export const Route = createFileRoute('/_app/admin/analytics')({
  component: AnalyticsPage,
  staticData: {
    breadcrumbs: [{ label: 'Analytics' }],
  } satisfies RouteStaticData,
})

function AnalyticsPage() {
  const funnelQuery = trpc.admin.analytics.launchFunnel.useQuery()
  const notificationQuery = trpc.notifications.analytics.useQuery()
  const analytics = notificationQuery.data as NotificationAnalytics | undefined

  const numberFormatter = useMemo(() => new Intl.NumberFormat(), [])
  const percentFormatter = useMemo(
    () => new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }),
    []
  )

  const typeData = useMemo(() => {
    if (!analytics) return []
    return Object.entries(analytics.byType).map(([type, dataset]) => ({
      type: type.charAt(0).toUpperCase() + type.slice(1),
      total: dataset.total,
      read: dataset.read,
      clicked: dataset.clicked,
      openRate: dataset.total > 0 ? (dataset.read / dataset.total) * 100 : 0,
    }))
  }, [analytics])

  const funnelStages = funnelQuery.data?.stages ?? []
  const hasNotificationData = analytics && analytics.total > 0

  return (
    <ReportRouteShell>
      <div className="grid gap-6">
        <section className="grid gap-4">
          <div>
            <p className="app-kicker">Launch funnel</p>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight">Startup launch signals</h1>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
              First-party operational counts for landing to signup, setup, live access, and first
              booking. Connect production PostHog to replace the landing proxy with real visit
              events.
            </p>
          </div>

          {funnelQuery.isLoading ? (
            <AnalyticsSkeleton />
          ) : funnelQuery.isError ? (
            <Callout variant="destructive">
              <AlertTriangle className="size-5" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">
                Failed to load launch funnel. {funnelQuery.error.message}
              </p>
              <Button variant="outline" size="sm" onClick={() => funnelQuery.refetch()}>
                Retry
              </Button>
            </Callout>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-3">
                <MetricCard
                  label="Total appointments"
                  value={funnelQuery.data?.metrics.totalAppointments ?? 0}
                  helper="All appointment records"
                />
                <MetricCard
                  label="Public operators"
                  value={funnelQuery.data?.metrics.publicOperators ?? 0}
                  helper="Artifacts currently public"
                />
                <MetricCard
                  label="Operators with bookings"
                  value={funnelQuery.data?.metrics.activePaygOperators ?? 0}
                  helper="Positive booking usage"
                />
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Landing to first booking</CardTitle>
                  <CardDescription>
                    Conversion is measured against the previous stage.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={funnelStages}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                        {funnelStages.map((stage, index) => (
                          <Cell key={stage.key} fill={`var(--chart-${Math.min(index + 1, 5)})`} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Funnel detail</CardTitle>
                  <CardDescription>
                    Counts are derived from current app and API data.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Stage</TableHead>
                        <TableHead className="text-right">Count</TableHead>
                        <TableHead className="text-right">Previous conversion</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {funnelStages.map((stage) => (
                        <TableRow key={stage.key}>
                          <TableCell className="font-medium">{stage.label}</TableCell>
                          <TableCell className="text-right">
                            {numberFormatter.format(stage.value)}
                          </TableCell>
                          <TableCell className="text-right">
                            {stage.conversionFromPrevious === null
                              ? 'Baseline'
                              : `${percentFormatter.format(stage.conversionFromPrevious)}%`}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {funnelQuery.data?.notes.length ? (
                    <ul className="mt-4 list-inside list-disc space-y-1 text-xs text-muted-foreground">
                      {funnelQuery.data.notes.map((note) => (
                        <li key={note}>{note}</li>
                      ))}
                    </ul>
                  ) : null}
                </CardContent>
              </Card>
            </>
          )}
        </section>

        <section className="grid gap-4">
          <div>
            <p className="app-kicker">Notification signals</p>
            <h2 className="mt-2 text-xl font-semibold tracking-tight">Operator engagement</h2>
          </div>

          {notificationQuery.isLoading ? (
            <AnalyticsSkeleton />
          ) : notificationQuery.isError ? (
            <Callout variant="destructive">
              <AlertTriangle className="size-5" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">
                Failed to load notification analytics. {notificationQuery.error.message}
              </p>
            </Callout>
          ) : !hasNotificationData ? (
            <Callout variant="info" className="border-dashed">
              <BarChart3 className="size-5" aria-hidden="true" />
              <CardTitle className="text-base">No notification analytics yet</CardTitle>
              <CardDescription>
                Trigger outbound notifications to populate engagement baselines.
              </CardDescription>
            </Callout>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-4">
                <MetricCard label="Total sent" value={analytics.total} helper="All notifications" />
                <MetricCard
                  label="Open rate"
                  value={analytics.openRate}
                  helper={`${analytics.read} of ${analytics.total} read`}
                  suffix="%"
                />
                <MetricCard
                  label="Click rate"
                  value={analytics.clickRate}
                  helper={`${analytics.clicked} of ${analytics.withAction} clicked`}
                  suffix="%"
                />
                <MetricCard
                  label="Unread"
                  value={analytics.unread}
                  helper="Pending notifications"
                />
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Performance by type</CardTitle>
                  <CardDescription>
                    Read and click-through by notification category.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Read</TableHead>
                        <TableHead className="text-right">Clicked</TableHead>
                        <TableHead className="text-right">Open rate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {typeData.map((row) => (
                        <TableRow key={row.type}>
                          <TableCell>{row.type}</TableCell>
                          <TableCell className="text-right">
                            {numberFormatter.format(row.total)}
                          </TableCell>
                          <TableCell className="text-right">
                            {numberFormatter.format(row.read)}
                          </TableCell>
                          <TableCell className="text-right">
                            {numberFormatter.format(row.clicked)}
                          </TableCell>
                          <TableCell className="text-right">
                            {percentFormatter.format(row.openRate)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </section>
      </div>
    </ReportRouteShell>
  )
}

function MetricCard({
  label,
  value,
  helper,
  suffix = '',
}: {
  label: string
  value: number
  helper: string
  suffix?: string
}) {
  const formatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 })

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold">
          {formatter.format(value)}
          {suffix}
        </div>
        <p className="text-xs text-muted-foreground">{helper}</p>
      </CardContent>
    </Card>
  )
}

function AnalyticsSkeleton() {
  return (
    <div className="grid gap-4 md:grid-cols-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <Card key={`analytics-skeleton-${index}`}>
          <CardHeader className="space-y-2 pb-2">
            <Skeleton className="h-4 w-24" />
          </CardHeader>
          <CardContent className="space-y-2">
            <Skeleton className="h-7 w-20" />
            <Skeleton className="h-3 w-32" />
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
